<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import Map from '@/components/map/Map.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';

import { useI18n } from 'vue-i18n';
import { useFieldValue, useFormContext, useIsFormDirty } from 'vee-validate';
import { Notify, useQuasar } from 'quasar';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { formSaveImageGuard } from '@/helpers/form.helper';
import type { Locales } from '@/services/types/api.types';
import type { MapLayerConfig, MapLayerPreview, MapSize, MapStepConfig } from '@/components/map/mapLayer.types';

const props = defineProps<{
  fieldPrefix: string;
  config?: MapStepConfig | null;
  editLocale: Locales;
  uploadLayer: (file: File) => Promise<boolean>;
  deleteLayer: (layerId: number) => Promise<boolean>;
  /** The size of the participant's map, which the boundary canvas is drawn at (see `Map.vue`). */
  participantMapSize: MapSize;
}>();

const { t } = useI18n();
const $q = useQuasar();

const { setFieldValue } = useFormContext();
const isFormDirty = useIsFormDirty();
const styleField = useFieldValue<string | null | undefined>(`${props.fieldPrefix}.mapStyle`);

const mapStyleOptions = computed(() => [
  { label: t('map.style.street'), value: 'mapbox://styles/mapbox/streets-v12' },
  { label: t('map.style.outdoors'), value: 'mapbox://styles/mapbox/outdoors-v12' },
  { label: t('map.style.light'), value: 'mapbox://styles/mapbox/light-v11' },
  { label: t('map.style.satellite'), value: 'mapbox://styles/mapbox/satellite-v9' },
]);

const selectedStyle = ref<string | null | undefined>(props.config?.mapStyle ?? null);
const bounds = ref(props.config?.bounds ?? undefined);
const zoom = ref<number | null | undefined>(props.config?.zoom ?? null);

watch(styleField, (value) => {
  if (value !== undefined) {
    selectedStyle.value = value;
  }
});

const layersField = useFieldValue<MapLayerConfig[] | null | undefined>(`${props.fieldPrefix}.layers`);
const layers = computed<MapLayerConfig[]>(() => layersField.value ?? []);

const layerVisibility = ref<Record<number, boolean>>({});
const layerNames = ref<Record<number, string>>({});
const layerDeletionState = ref<Record<number, boolean>>({});

watch(
  [layers, () => props.editLocale],
  ([current]) => {
    const visibility: Record<number, boolean> = {};
    const names: Record<number, string> = {};
    for (const layer of current) {
      if (typeof layer.id !== 'number') continue;
      visibility[layer.id] = layer.defaultVisibility !== false;
      names[layer.id] = localizedName(layer);
    }
    layerVisibility.value = visibility;
    layerNames.value = names;
  },
  { immediate: true, deep: true },
);

function localizedName(layer: MapLayerConfig): string {
  const name = layer.name as Record<string, string | null> | null | undefined;
  return name?.[props.editLocale] ?? '';
}

function layerLabel(layer: MapLayerConfig): string {
  const name = layer.name as Record<string, string | null> | null | undefined;
  return localizedName(layer) || name?.en || name?.fr || t('map.map-layers.unnamed', { id: layer.id });
}

function indexOfLayer(layerId: number): number {
  return layers.value.findIndex((layer) => layer.id === layerId);
}

function onVisibilityChange(layerId: number, value: boolean) {
  const index = indexOfLayer(layerId);
  if (index === -1) return;
  setFieldValue(`${props.fieldPrefix}.layers[${index}].defaultVisibility`, value !== false);
}

function onNameChange(layerId: number, value: string) {
  const index = indexOfLayer(layerId);
  if (index === -1) return;
  setFieldValue(`${props.fieldPrefix}.layers[${index}].name.${props.editLocale}`, value);
}

const layersForMap = computed<MapLayerPreview[]>(() =>
  layers.value.reduce<MapLayerPreview[]>((acc, layer) => {
    if (typeof layer.id !== 'number') {
      return acc;
    }
    acc.push({
      id: layer.id,
      file: layer.file ? { ...layer.file } : undefined,
      visible: layerVisibility.value[layer.id] ?? layer.defaultVisibility !== false,
      defaultVisibility: layer.defaultVisibility,
    });
    return acc;
  }, []),
);

const layerFile = ref<File | null>(null);
const layerFilePicker = ref<{ pickFiles: (evt?: Event) => void } | null>(null);
const importInProgress = ref(false);

// Importing or deleting a layer saves it at once and reloads the record, which resets the whole
// form: warn first, as an image upload does, rather than drop the admin's unsaved edits.
function openLayerPicker() {
  formSaveImageGuard(isFormDirty.value, () => layerFilePicker.value?.pickFiles());
}

function onDeleteLayerClick(layer: MapLayerConfig) {
  formSaveImageGuard(isFormDirty.value, () => confirmDeleteLayer(layer));
}

function onLayerFileSelected(val: File | File[] | null) {
  const file = Array.isArray(val) ? val[0] : val;
  if (file) {
    importLayer(file);
  }
}

async function importLayer(file: File) {
  importInProgress.value = true;
  try {
    await wrapSavingOperation(
      async () => await props.uploadLayer(file),
      () => {
        Notify.create({ color: 'positive', message: t('map.import.success'), position: 'top', timeout: 1500 });
      },
    );
  } finally {
    importInProgress.value = false;
    layerFile.value = null;
  }
}

function updateLayerDeletionState(id: number, value: boolean) {
  if (value) {
    layerDeletionState.value = { ...layerDeletionState.value, [id]: true };
  } else {
    const { [id]: _removed, ...rest } = layerDeletionState.value;
    layerDeletionState.value = rest;
  }
}

function isLayerDeleting(id: number): boolean {
  return Boolean(layerDeletionState.value[id]);
}

function confirmDeleteLayer(layer: MapLayerConfig) {
  if (typeof layer.id !== 'number') return;
  const name = layerLabel(layer);
  $q.dialog({
    title: t('map.map-layers.delete.title'),
    message: t('map.map-layers.delete.message', { name }),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(async () => {
    updateLayerDeletionState(layer.id, true);
    await wrapSavingOperation(
      async () => await props.deleteLayer(layer.id),
      () => {
        updateLayerDeletionState(layer.id, false);
        Notify.create({
          color: 'positive',
          message: t('map.map-layers.delete.success'),
          position: 'top',
          timeout: 1500,
        });
      },
      () => {
        updateLayerDeletionState(layer.id, false);
      },
    );
  });
}

function sendMapBounds(mapConfig: { bounds: MapStepConfig['bounds']; zoom: number }) {
  setFieldValue(`${props.fieldPrefix}.bounds`, mapConfig.bounds);
  setFieldValue(`${props.fieldPrefix}.zoom`, mapConfig.zoom);
}
</script>
<template>
  <div class="map-definition">
    <div class="map-definition__boundary">
      <div class="map-definition__boundary-title q-table__title">{{ $t('map.set-boundary.title') }}</div>
      <div class="admin-map__instructions-text">
        {{ $t('map.set-boundary.text') }}
      </div>
    </div>
    <div class="admin-map-row">
      <Map
        class="admin-map"
        @set-bounds="sendMapBounds"
        :selectedStyle="selectedStyle ?? undefined"
        :bounds="bounds"
        :initial-zoom="zoom"
        :layers="layersForMap"
        :participant-map-size="props.participantMapSize"
      />
      <div class="map-layers-panel-wrapper">
        <div class="map-layers-panel">
          <div class="q-table__title">{{ $t('map.map-layers.title') }}</div>
          <div v-if="layers.length === 0" class="text-grey-7 q-mt-sm">{{ $t('map.map-layers.empty') }}</div>
          <div class="map-layers-list q-mt-sm">
            <div v-for="l in layers" :key="l.id" class="map-layers-list__item">
              <q-checkbox
                class="map-layers-list__checkbox"
                v-model="layerVisibility[l.id]"
                :aria-label="$t('map.map-layers.visible')"
                @update:model-value="(value) => onVisibilityChange(l.id, value)"
              />
              <q-input
                class="map-layers-list__name"
                dense
                outlined
                v-model="layerNames[l.id]"
                :label="$t('map.map-layers.name')"
                @update:model-value="(value) => onNameChange(l.id, String(value ?? ''))"
              />
              <div class="map-layers-list__toolbar">
                <q-btn
                  flat
                  dense
                  round
                  size="sm"
                  color="negative"
                  icon="delete"
                  :aria-label="$t('global.label.delete')"
                  :loading="isLayerDeleting(l.id)"
                  :disable="isLayerDeleting(l.id)"
                  @click="onDeleteLayerClick(l)"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="map-layers-panel__actions">
          <q-btn color="primary" :loading="importInProgress" :label="$t('map.import.layer')" @click="openLayerPicker" />
          <q-file
            v-model="layerFile"
            ref="layerFilePicker"
            style="display: none"
            :multiple="false"
            :accept="'.geojson,application/geo+json,application/vnd.geo+json,application/json'"
            @update:model-value="onLayerFileSelected"
          />
        </div>
      </div>
    </div>
    <div class="map-definition__styles">
      <localized-select
        :name="`${props.fieldPrefix}.mapStyle`"
        :label="$t('map.style.map-style')"
        :editLocale="editLocale"
        :options="mapStyleOptions"
        emit-value
        map-options
        outlined
        @change="
          (value) => {
            selectedStyle = value;
          }
        "
      />
      <slot name="style-actions" />
    </div>
  </div>
</template>
<style lang="scss">
.map-definition {
  display: flex;
  flex-direction: column;

  &__styles {
    margin: 20px 0;
    gap: 5px;
  }

  .admin-map-row {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  /* Fluid rather than a fixed 450px: the canvas keeps its shape at whatever width it gets, so letting it
     take the room available is what makes the framed area large enough to work with. `Map.vue`
     watches its own container for the resizes this causes, and stops growing at the participant's map
     size, past which the layers panel takes the rest of the row. */
  .admin-map {
    flex: 2 1 480px;
    min-width: 300px;
  }

  .map-layers-panel-wrapper {
    flex: 1;
    min-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .map-layers-panel {
    background: $color-neutral-grey-200;
    border-radius: 8px;
    padding: 10px;

    &__subtext {
      font-size: 12px;
      color: $color-neutral-grey-400-50;
    }
  }

  .map-layers-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .map-layers-list__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .map-layers-list__name {
    flex: 1;
  }

  .map-layers-list__toolbar {
    display: flex;
    align-items: center;
  }

  .map-layers-panel__actions {
    display: flex;
    justify-content: flex-start;
  }

  @media (max-width: 600px) {
    .admin-map {
      flex: 1 1 100%;
      min-width: 0;
    }
  }
}
</style>
